
import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { API_URL } from '..';

const weekDays = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

const ViewStaffLayer = ({ data }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formValues, setFormValues] = useState({
    name: data.name,
    email: data.email,
    phone: data.phone,
    role: { value: data.role || 'electrician', label: data.role || 'Electrician' },
    status: data.status || 'active',
    payroll_type: data.payroll_type || '',
    rate: data.rate || '',
  });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ status: false, reason: '', desc: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schedules, setSchedules] = useState(
    data.schedules || weekDays.map(day => ({
      day, isWorkingDay: true, start_time: '', end_time: '', late_margin: '', early_margin: '', total_hours: ''
    }))
  );

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'user', label: 'User' },
    { value: 'electrician', label: 'Electrician' },
    
  ];

  // ========== PERSONAL INFO ==========
  const validatePersonalInfo = () => {
    const newErrors = {};
    if (!formValues.name) newErrors.name = 'Name is required';
    if (!formValues.email || !/\S+@\S+\.\S+/.test(formValues.email)) newErrors.email = 'Valid email is required';
    if (!formValues.phone) newErrors.phone = 'Phone is required';
    if (["user", "electrician", "workforce"].includes(formValues.role.value)) {
      if (!formValues.payroll_type) newErrors.payroll_type = 'Select payroll type';
      if (!formValues.rate) newErrors.rate = 'Rate is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    if (!validatePersonalInfo()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/user/update-user/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formValues.name,
          email: formValues.email,
          phone: formValues.phone,
          role: formValues.role.value,
          status: formValues.status,
          payroll_type: formValues.payroll_type,
          rate: formValues.rate,
        }),
      });
      const resData = await response.json();
      if (response.status === 200) {
        setMessage({ status: true, reason: 'success', desc: 'Staff information updated successfully' });
        setIsEditMode(false);
      } else throw new Error(resData.message || 'Failed to update staff');
    } catch (err) {
      setMessage({ status: true, reason: 'error', desc: `Failed: ${err.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const toggleEditMode = () => {
    setFormValues({
      name: data.name,
      email: data.email,
      phone: data.phone,
      role: { value: data.role || 'electrician', label: data.role || 'Electrician' },
      status: data.status || 'active',
      payroll_type: data.payroll_type || '',
      rate: data.rate || '',
    });
    setIsEditMode(!isEditMode);
    setErrors({});
    setMessage({ status: false, reason: '', desc: '' });
  };

  // ========== PASSWORD ==========
  const validatePassword = () => {
    const newErrors = {};
    if (!passwordForm.newPassword) newErrors.newPassword = 'Current password is required';
    if (!passwordForm.confirmPassword) newErrors.confirmPassword = 'New password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/user/change-password/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_password: passwordForm.newPassword,
          new_password: passwordForm.confirmPassword,
        }),
      });
      const result = await res.json();
      if (res.status === 200) {
        setMessage({ status: true, reason: 'success', desc: 'Password updated successfully' });
        setPasswordForm({ newPassword: '', confirmPassword: '' });
      } else throw new Error(result.message);
    } catch (err) {
      setMessage({ status: true, reason: 'error', desc: `Failed: ${err.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========== SCHEDULE ==========
  const handleScheduleChange = (index, field, value) => {
    const updated = [...schedules];
    updated[index][field] = value;
    if (field === 'isWorkingDay' && !value) {
      updated[index] = {
        ...updated[index],
        start_time: '',
        end_time: '',
        late_margin: '',
        early_margin: '',
        total_hours: '',
      };
    }
    if (field === 'start_time' || field === 'end_time') {
      const s = updated[index];
      if (s.start_time && s.end_time) {
        const start = new Date(`2024-01-01T${s.start_time}`);
        const end = new Date(`2024-01-01T${s.end_time}`);
        const hours = (end - start) / (1000 * 60 * 60);
        updated[index].total_hours = hours > 0 ? hours.toFixed(2) : '0';
      }
    }
    setSchedules(updated);
  };

  const applyToAllDays = () => {
    if (!schedules[0].start_time || !schedules[0].end_time) return;
    const base = schedules[0];
    const updated = schedules.map((sch, i) =>
      i === 0
        ? sch
        : {
            ...sch,
            isWorkingDay: base.isWorkingDay,
            start_time: base.start_time,
            end_time: base.end_time,
            late_margin: base.late_margin,
            early_margin: base.early_margin,
            total_hours: base.total_hours,
          }
    );
    setSchedules(updated);
  };

  const saveSchedules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/update/schedule/${data.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedules }),
      });
      const result = await res.json();
      if (res.status === 200) {
        setMessage({ status: true, reason: 'success', desc: 'Schedules updated successfully' });
      } else throw new Error(result.message);
    } catch (err) {
      setMessage({ status: true, reason: 'error', desc: `Failed: ${err.message}` });
    }
  };

  useEffect(() => {
    if (data && data.schedules) {
      const normalized = weekDays.map(day => {
        const existing = data.schedules.find(s => s.day === day);
        return existing || { day, isWorkingDay: true, start_time: '', end_time: '', late_margin: 0, early_margin: 0, total_hours: '' };
      });
      setSchedules(normalized);
    }
  }, [data]);

  // ========== UI ==========
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white">
              <h6 className="h6 mb-0">View Staff</h6>
            </div>
            <div className="card-body">
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item"><button className={`nav-link ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}>Personal Info</button></li>
                <li className="nav-item"><button className={`nav-link ${activeTab === 'password' ? 'active' : ''}`} onClick={() => setActiveTab('password')}>Password</button></li>
                <li className="nav-item"><button className={`nav-link ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>Schedules</button></li>
              </ul>

              {message.status && (
                <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-4`}>
                  {message.desc}
                  <button type="button" className="btn-close" onClick={() => setMessage({ ...message, status: false })}></button>
                </div>
              )}

              {/* PERSONAL INFO TAB */}
              {activeTab === 'personal' && (
                <div className="p-3">
                  {isEditMode ? (
                    <form onSubmit={handlePersonalInfoSubmit}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Name</label>
                          <input type="text" value={formValues.name} onChange={(e) => handleInputChange('name', e.target.value)} className={`form-control ${errors.name ? 'is-invalid' : ''}`} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Email</label>
                          <input type="email" value={formValues.email} onChange={(e) => handleInputChange('email', e.target.value)} className={`form-control ${errors.email ? 'is-invalid' : ''}`} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Phone</label>
                          <input type="text" value={formValues.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={`form-control ${errors.phone ? 'is-invalid' : ''}`} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Role</label>
                          <Select options={roleOptions} value={formValues.role} onChange={(selected) => handleInputChange('role', selected)} />
                        </div>

                        {["user", "electrician", "workforce"].includes(formValues.role.value) && (
                          <div className="col-12 border rounded p-3 bg-light mt-3">
                            <label className="form-label fw-bold">Payroll Type</label>
                            <div className="d-flex gap-4 mb-3">
                              <div className="form-check">
                                <input className="form-check-input" type="radio" name="payroll_type" value="daily" checked={formValues.payroll_type === 'daily'} onChange={(e) => handleInputChange('payroll_type', e.target.value)} />
                                <label className="form-check-label">Daily</label>
                              </div>
                              <div className="form-check">
                                <input className="form-check-input" type="radio" name="payroll_type" value="hourly" checked={formValues.payroll_type === 'hourly'} onChange={(e) => handleInputChange('payroll_type', e.target.value)} />
                                <label className="form-check-label">Hourly</label>
                              </div>
                            </div>
                            <label className="form-label fw-bold">Rate</label>
                            <input type="number" value={formValues.rate} onChange={(e) => handleInputChange('rate', e.target.value)} className={`form-control ${errors.rate ? 'is-invalid' : ''}`} placeholder="Enter rate" />
                          </div>
                        )}

                        <div className="col-md-6 mt-3">
                          <label className="form-label fw-bold">Status</label>
                          <div className="form-check form-switch mt-2">
                            <input className="form-check-input" type="checkbox" checked={formValues.status === 'active'} onChange={() => handleInputChange('status', formValues.status === 'active' ? 'inactive' : 'active')} />
                            <label className="form-check-label">{formValues.status === 'active' ? 'Active' : 'Inactive'}</label>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-4 gap-3">
                        <button type="button" className="btn btn-secondary" onClick={toggleEditMode}>Cancel</button>
                        <button type="submit" className="btn btn-success" disabled={isSubmitting}><Icon icon="simple-line-icons:check" className="me-2" />{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      <div className="row g-3">
                        <div className="col-md-6"><label className="form-label fw-bold">Name</label><input type="text" value={data.name} className="form-control" readOnly /></div>
                        <div className="col-md-6"><label className="form-label fw-bold">Email</label><input type="email" value={data.email} className="form-control" readOnly /></div>
                        <div className="col-md-6"><label className="form-label fw-bold">Phone</label><input type="text" value={data.phone} className="form-control" readOnly /></div>
                        <div className="col-md-6"><label className="form-label fw-bold">Role</label><input type="text" value={data.role} className="form-control" readOnly /></div>
                        {["user", "electrician", "workforce"].includes(data.role) && (
                          <>
                            <div className="col-md-6"><label className="form-label fw-bold">Payroll Type</label><input type="text" value={data.payroll_type || '-'} className="form-control" readOnly /></div>
                            <div className="col-md-6"><label className="form-label fw-bold">Rate</label><input type="text" value={data.rate || '-'} className="form-control" readOnly /></div>
                          </>
                        )}
                        <div className="col-md-6"><label className="form-label fw-bold">Status</label><input type="text" value={data.status} className="form-control" readOnly /></div>
                      </div>
                      <div className="d-flex justify-content-end mt-4">
                        <button className="btn btn-primary" onClick={toggleEditMode}><Icon icon="simple-line-icons:pencil" className="me-2" />Edit Staff</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PASSWORD TAB */}
              {activeTab === 'password' && (
                <div className="p-3">
                  <h5 className="mb-3">Change Password</h5>
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Current Password</label>
                        <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">New Password</label>
                        <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`} />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-4">
                      <button type="submit" className="btn btn-success" disabled={isSubmitting}><Icon icon="simple-line-icons:check" className="me-2" />{isSubmitting ? 'Updating...' : 'Update Password'}</button>
                    </div>
                  </form>
                </div>
              )}
                            {activeTab === 'schedule' && (
  <div className="p-3 rounded">
    <h2 className="h6 mb-4 d-flex justify-content-between align-items-center">
      Weekly Schedule
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        disabled={!schedules[0]?.start_time || !schedules[0]?.end_time}
        onClick={() => {
          const base = schedules[0];
          const updated = schedules.map((sch, i) =>
            i === 0
              ? sch
              : {
                  ...sch, // keep original day
                  isWorkingDay: base.isWorkingDay,
                  start_time: base.start_time,
                  end_time: base.end_time,
                  late_margin: base.late_margin,
                  early_margin: base.early_margin,
                  total_hours: base.total_hours,
                }
          );
          setSchedules(updated);
        }}
      >
        Apply to All Days
      </button>
    </h2>
    
    <div className="table-responsive">
      <table className="table table-bordered align-middle text-center">
        <thead className="table-light">
          <tr>
            <th>Day</th>
            <th>Working?</th>
            <th>Start</th>
            <th>End</th>
            <th>Late Margin</th>
            <th>Early Out</th>
            <th>Total Hours</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((sch, i) => (
            <tr key={i}>
              <td>{sch.day}</td>
              <td>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={sch.isWorkingDay}
                  onChange={(e) =>
                    handleScheduleChange(i, 'isWorkingDay', e.target.checked)
                  }
                />{' '}
                {sch.isWorkingDay ? 'Yes' : 'Off'}
              </td>
              <td>
                <input
                  type="time"
                  className="form-control"
                  value={sch.start_time || ''}
                  disabled={!sch.isWorkingDay}
                  onChange={(e) =>
                    handleScheduleChange(i, 'start_time', e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="time"
                  className="form-control"
                  value={sch.end_time || ''}
                  disabled={!sch.isWorkingDay}
                  onChange={(e) =>
                    handleScheduleChange(i, 'end_time', e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  value={sch.late_margin || 0}
                  disabled={!sch.isWorkingDay}
                  onChange={(e) =>
                    handleScheduleChange(i, 'late_margin', e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  value={sch.early_margin || 0}
                  disabled={!sch.isWorkingDay}
                  onChange={(e) =>
                    handleScheduleChange(i, 'early_margin', e.target.value)
                  }
                />
              </td>
              <td>{sch.total_hours || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="d-flex justify-content-end mt-4">
      <button
        className="btn btn-success"
        onClick={async () => {
          try {
            const res = await fetch(`${API_URL}/api/user/update/schedule/${data.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ schedules }),
            });
            const result = await res.json();
            if (res.status === 200) {
              setMessage({ status: true, reason: 'success', desc: 'Schedules updated successfully' });
            } else {
              throw new Error(result.message);
            }
          } catch (err) {
            setMessage({ status: true, reason: 'error', desc: `Failed: ${err.message}` });
          }
        }}
      >
        <Icon icon="simple-line-icons:check" className="me-2" />
        Save Schedule
      </button>
    </div>
  </div>
)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewStaffLayer;